import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { authApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import { ErrorBanner, InfoBanner } from "../components/Feedback";
import { Icon } from "../components/Icon";
import { MIN_LENGTH, PW_RULES, passwordStrength } from "../utils/password";

export function SignUpPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const strength = useMemo(() => passwordStrength(password), [password]);
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = !!email && password.length >= MIN_LENGTH && password === confirm && !busy;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (password.length < MIN_LENGTH) return setError(`Password must be at least ${MIN_LENGTH} characters.`);
    if (password !== confirm) return setError("Passwords do not match.");

    setBusy(true);
    try {
      const res = await authApi.signup(email, password);
      setMessage(res.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-up failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <span className="brand-mark">
          <Icon name="layers" size={22} />
        </span>
        Ticket Tracker
      </div>

      <div className="auth-card">
        <div className="auth-head">
          <h1>Create account</h1>
          <p>Email verification is required before you can log in.</p>
        </div>

        <form onSubmit={onSubmit}>
          {error && <ErrorBanner message={error} />}
          {message && <InfoBanner message={message} />}

          <label className="field">
            <span className="field-label">Email</span>
            <input
              type="email"
              placeholder="name@example.com"
              value={email}
              required
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">Password</span>
            <div className="input-affix">
              <input
                type={show ? "text" : "password"}
                placeholder="Minimum 8 characters"
                value={password}
                required
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="affix-btn"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Hide password" : "Show password"}
              >
                <Icon name={show ? "eyeOff" : "eye"} size={18} />
              </button>
            </div>

            {password && (
              <>
                <div className="row" style={{ gap: 10 }}>
                  <div className="pw-meter grow">
                    <span style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
                  </div>
                  <span className="pw-strength-label" style={{ color: strength.color }}>
                    {strength.label}
                  </span>
                </div>
                <ul className="pw-rules">
                  {PW_RULES.map((r) => {
                    const ok = r.test(password);
                    return (
                      <li key={r.id} className={ok ? "pw-rule ok" : "pw-rule"}>
                        <span className="tick">{ok && <Icon name="check" size={11} />}</span>
                        {r.label}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </label>

          <label className="field">
            <span className="field-label">Confirm password</span>
            <input
              type={show ? "text" : "password"}
              value={confirm}
              required
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
            />
            {mismatch && <span className="field-error">Passwords do not match.</span>}
          </label>

          <button type="submit" className="btn-primary btn-block" disabled={!canSubmit}>
            {busy ? "Creating…" : "Sign up"}
          </button>
        </form>

        <div className="auth-divider">or</div>
        <p className="auth-alt">
          Already registered? <Link to="/login">Log in →</Link>
        </p>
      </div>
    </div>
  );
}
